/**
 * User profile storage for dashboard.
 * Profile data is persisted in localStorage and synced from login response.
 */

import { COUNTRIES } from '@/utilities/countries'

export const PROFILE_STORAGE_KEY = 'gallium_user_profile'

export interface UserProfile {
  name: string
  firstName: string
  lastName: string
  email: string
  phoneNumber: string
  location: string
  region: string
  company: string
  taxId: string
}

const defaultProfile: UserProfile = {
  name: '',
  firstName: '',
  lastName: '',
  email: '',
  phoneNumber: '',
  location: '',
  region: '',
  company: '',
  taxId: '',
}

export function getStoredProfile(): UserProfile {
  if (typeof window === 'undefined') return defaultProfile
  try {
    const raw = localStorage.getItem(PROFILE_STORAGE_KEY)
    if (!raw) return defaultProfile
    const parsed = JSON.parse(raw) as Partial<UserProfile>
    return { ...defaultProfile, ...parsed }
  } catch {
    return defaultProfile
  }
}

/** Country name from dealer profile (`region` in gallium_user_profile — ERP stores country there). */
export function getStoredCountryName(): string {
  const profile = getStoredProfile()
  const hint = profile.region.trim()
  if (!hint) return ''

  const match = COUNTRIES.find(
    (country) =>
      country.name.toLowerCase() === hint.toLowerCase() ||
      country.code.toLowerCase() === hint.toLowerCase(),
  )

  return match?.name ?? hint
}

/** Phone from dealer profile (`phoneNumber` in gallium_user_profile). */
export function getStoredPhoneNumber(): string {
  const profile = getStoredProfile()
  return profile.phoneNumber.replace(/\s/g, '').trim()
}

/** First/last name from dealer profile (`gallium_user_profile`). */
export function getStoredNameParts(): { firstName: string; lastName: string } {
  const profile = getStoredProfile()
  const first = profile.firstName.trim()
  const last = profile.lastName.trim()

  if (first && last) {
    return { firstName: first, lastName: last }
  }

  if (first) {
    return { firstName: first, lastName: last || first }
  }

  const name = profile.name.trim()
  if (name) {
    const parts = name.split(/\s+/).filter(Boolean)
    if (parts.length === 1) {
      return { firstName: parts[0]!, lastName: parts[0]! }
    }
    return { firstName: parts[0]!, lastName: parts.slice(1).join(' ') }
  }

  return { firstName: '', lastName: '' }
}

export function setStoredProfile(profile: Partial<UserProfile>): void {
  if (typeof window === 'undefined') return
  try {
    const current = getStoredProfile()
    const merged = { ...current, ...profile }
    localStorage.setItem(PROFILE_STORAGE_KEY, JSON.stringify(merged))
  } catch {
    // Ignore
  }
}

export function clearStoredProfile(): void {
  if (typeof window === 'undefined') return
  try {
    localStorage.removeItem(PROFILE_STORAGE_KEY)
  } catch {
    // Ignore
  }
}

/**
 * Initialize profile from login response (full_name, first_name, last_name, email, mobile_no, location, region, company, tax_id).
 */
export function initProfileFromLogin(data: {
  full_name?: string | null
  first_name?: string | null
  last_name?: string | null
  email?: string | null
  mobile_no?: string | null
  location?: string | null
  region?: string | null
  company?: string | null
  tax_id?: string | null
}): void {
  const fullName = typeof data.full_name === 'string' ? data.full_name.trim() : ''
  const firstName = typeof data.first_name === 'string' ? data.first_name.trim() : ''
  const lastName = typeof data.last_name === 'string' ? data.last_name.trim() : ''
  const email = typeof data.email === 'string' ? data.email.trim() : ''
  const phoneNumber = typeof data.mobile_no === 'string' ? data.mobile_no.trim() : ''
  const location = typeof data.location === 'string' ? data.location.trim() : ''
  const region = typeof data.region === 'string' ? data.region.trim() : ''
  const company = typeof data.company === 'string' ? data.company.trim() : ''
  const taxId = typeof data.tax_id === 'string' ? data.tax_id.trim() : ''

  let resolvedFirstName = firstName
  let resolvedLastName = lastName
  if (!resolvedFirstName && fullName) {
    const parts = fullName.split(/\s+/).filter(Boolean)
    resolvedFirstName = parts[0] ?? ''
    resolvedLastName = parts.slice(1).join(' ') || parts[0] || ''
  }

  setStoredProfile({
    name: fullName || resolvedFirstName,
    firstName: resolvedFirstName,
    lastName: resolvedLastName,
    email,
    phoneNumber,
    location,
    region,
    company,
    taxId,
  })
}
